import { commonApi, OffsetLimit, StatusPagesResponse, StatusResponse } from './api';
import { FSObjectCategory } from './content_v1';
import { AccountListElement, UserProfileMinimal } from './users_v1';

// Пока определил три разных доступа:
export enum SdnPermissionType {
    DENY = 0,
    READ = 1,   // _("SDN-CONST-PERM-READ", "Чтение")
    WRITE = 2,   // _("SDN-CONST-PERM-WRITE", "Запись")
    FULLCONTROL = 255, // _("SDN-CONST-PERM-FULLCONTROL", "Полный доступ")
}

// Поддерживается 2 вида ссылок, presigned url на отдельный файл или public - как на файлы так и на папки.
export enum SdnLinkType {
    PUBLIC = 0, // _("SDN-CONST-LINK-PUBLIC", "Ссылка на публичный интерфейс СХД"), { 'min_ttl': 60, 'max_ttl': 604800 }
    PRESIGNED = 1, // _("SDN-CONST-LINK-PRESIGNED", "Подписанная ссылка на файл"), { 'min_ttl': 60, 'max_ttl': 604800 }
}

export enum SdnRefType {
    NULL = 0, // 0=null(не ясно/пока не должно использоваться),
    SYSTEM = 1, // 1=system(системный/пока не используется),
    USER = 2, // 2=user(пользователь создал),
    LINK = 3, // 3=link(ссылка/пока не используется),
    ORDER = 4, // 4=order(по заявке)
}

export interface SdnReference {
    ref_type: SdnRefType,
    ref_id: number,
}

export interface AccessKeyResponse {
    accesskey: string,
    secretkey: string,
    token: string | undefined,
    expiry: string,
}

export interface AccessPermanentKeyResponse {
    accesskey: string,
    secretkey: string,
    user: UserProfileMinimal
    account: AccountListElement,
    expire?: string
}

export interface AccessInviteKeyResponse {
    'sended': boolean, // - если приглашение уже было отправлено, тут будет false
    'expire': string, // - срок действия приглашения
    'user': number,
    'ttl': number,
    'url': string,
    'qr': string
}

export interface AccessGroup {
    group: string,
    displayname: string,
    editable: boolean,
    account: number
}

export enum AuditOpType {
    NULL = 0, // "Операция с СХД"
    GET = 1, // "Скачивание"
    PUT = 2, // "Загрузка"
    DELETE = 3, // "Удаление"
    PERMSET = 4, // "Установка разрешения"
    PERMDEL = 5, // "Удаление разрешения"
    PERMCHG = 6, // "Изменение разрешения"
    LINKADD = 7, // "Выдана ссылка"
    LINKDEL = 8, // "Отозвана ссылка"
    M_ZIPLIST = 9, // "Просмотр архива"
    M_ZIPEXTRACT = 10, // "Распаковка архива"
    M_RENAME = 11, // "Переименование объектов"
    M_MOVE = 12, // "Перемещение объектов"
    M_COPY = 13, // "Копирование объектов"
    M_SAVETODISK = 14, // "Сохранение на диск"
    M_ZIP = 15, // "Создание архива"
    GET_ZIP_ARCHIVE = 18, // "Скачивание ZIP архива"
}

export interface SdnAudit {
    operation: AuditOpType,
    time: string,
    prefix: string,
    etag: string | null,
    versionid: string | null,
    details: {
        source?: string | string[]
        //# Для операций копирования/переименования/перемещения/сохранения на диск - "source" всегда массив
        //# Для операций просмотр архива/извлечение из архива - "source" - строка, имя архива
        //# Для операции извлечения архива - "source" - массив
        destination?: string // - строка куда копировать/перемещать/переименовывать/создавать архив(название), может и не быть для некоторых операций
        pattern?: string[] // - массив для операций извлечения из архива
    },
    opsize: number,
    opspeed: number,
    disk: number,
    user: UserProfileMinimal,
    account: AccountListElement,
    ip?: string,
}

export interface SdnCommand {
    command: SdnOperationType, // str, обязательный, тип операции
    source?: string, // str, обязательный, источник, указывается в виде bucket_name/path/
    sources?: string[], // list[str], список источников, указывается в виде bucket_name/path/, нужно указать либо source либо sources (можно и то и то, тогда они суммируются)
    destination?: string, // str, обязательный для операций копирования/переименования/перемещения/извлечения из архивов, тоже в виде bucket_name/path/
    patterns?: string[], // list[str], список шаблонов (в формате glob) файлов и папок, которые нужно извлечь из архива, по-умолчанию ['*']
    timeout?: number, // int, необязательный, сколько ожидать реакции пользователя на запрос, в секундах, с момента создания сообщения о запросе действия (по-уумолчанию 600)
}


export interface SdnDisk {
    disk_id: number,                         // - идентификатор диска (= идентификатору аккаунта)
    account: {
        id: number,
        rtype: number,
        name: string,
        nickname: string,
        org: {
            id: number,
            name: string,
            taxnum: string
        },
        owner: {
            id: number,
            email: string,
            phone: string,
            surname: string,
            hide_fio: boolean,
            lastname: string,
            firstname: string,
            hide_email: boolean,
            hide_phone: boolean
        },
        logo: string | null
    },
    bucket: string,                  // - название бакета в minio
    shared: boolean,
    access_locked: boolean,
    write_locked: boolean,
    low_freespace: boolean,
}

export interface SdnLinkAuth {
    auth: {
        email: string,
        group: number | null,
        type: SdnLinkType,    //   - тип авторизации 1- неавторизованный email, 0 - пользователь есть в медиабазе
        user: number | null
    },
    config: {   //                           - настройки s3
        'access_type': 'path-style' | 'domain-style',
        enabled: boolean,
        s3api: string,
    },
    link: {                         //               - Информация о ссылке
        account: {
            name: string,
            orgname: string
        },
        added: string,
        expire: string,
        id: number,
        prefixes: string[]
    },
    session: { //  - Информация о сессии, если сессия уже авторизована (ранее), тогда verified = True, ну и access,secret и signing будут проставлены
        accesskey: string | null,
        expire: string,
        host: string,
        otpexpire: string | null,
        secretkey: string | null,
        signing: string | null,
        verified: boolean
    }
}

export interface SdnLinkBase {
    id: number,
    added: string,
    expire: string, // '2025-12-20T05:46:42.146232+03:00',
    account: number,
    user: number,
    comment: string
    type: SdnLinkType,
    prefixes: string[],
    alimit: number,
}

export interface SdnLink extends SdnLinkBase {
    subjects: {
        email: string,
        user: number | null,
        group: number | null,
        alimit: number,
        aused: number,
        link: string,
        qr: string,
    }[]
}

export interface SdnLinkMin extends SdnLinkBase {
    subjects: string[]
}

export interface SdnLinkPublic {
    id: number,
    account: {
        name: string,
        orgname?: string
    },
    added: string, // '2026-02-02T10:04:24.146287+03:00',
    expire: string, //'2026-02-03T10:04:24.131848+03:00',
    prefixes: string[], // 'account1/v2/'
    config: {
        enabled: boolean,
        s3api: string, // 'http://127.0.0.1:9000',
        'access-type': 'path-style' | 'domain-style',
        accesskey: string,
        secretkey: string,
    }
}

export interface SdnLinkOTP {
    email: string,
    created: string, // - Когда код OTP сгенерирован
    expire: string, //- Когда код OTP станет недействительным
    sended: true, // - Признак того что код был отправлен, пока текущий код не станет недействительным, тут будет выдавать false на каждый вызов
}

export interface SdnLinkRequest {
    id?: number,
    type: SdnLinkType,  // int, обязательный, тип ссылки
    expire_in: number,    //  int, необязательный, срок действия ссылки в секундах (по-умолчанию max_ttl для соответствующего типа)
    comment: string,        //  str, необязательный, комментарий
    prefixes: string[]        //  list[str], обязательный, список префиксов (account111/prefix), которые будут доступны по ссылке, для типа presigned только один файл
    subjects: string[]        //  list[str], обязательный, список email-ов
    alimit: number | null        //  int или null, ограничение на кол-во заходов по ссылке
}

export interface SdnMeta {
    disk: number,
    filename: string, // '/20x40-6H-SP.zip',
    prefix: string, // 'account169/20x40-6H-SP.zip',
    size: number,
    etag: string, // 'bdb8fd40bb3fb08b3d70cb1dd3f2e4f1',
    versionid: string, // 'd4d6bef9-18a1-4417-886d-929d20c54c63',
    attributes: {
        'attr.cksum.md5': string, // 'bdb8fd40bb3fb08b3d70cb1dd3f2e4f1',
        'attr.cksum.valid': boolean,
        'attr.cksum.errors': string | null,
        'attr.cksum.sha512': string, // '414cbc0b22ee55656d0583cd39a37ac993d8c7ffbabed894d91f4007f5cfc5891f2162ebb0e3bc2570cedcdd777cac86bc816a9ba381abf15b37995365e3cc94',
        'attr.cksum.verified_at': string, // '2026-01-13T14:20:33.041107+00:00'
        // 2. Orbox (по сути соответвуют тому что и было раньше через отдельный вызов):
        'attr.orbox.checked'?: boolean,
        'attr.orbox.passed'?: boolean,
        'attr.orbox.report'?: string,
        'attr.orbox.videocodec'?: string
        'attr.orbox.videocodec.passed'?: boolean,
        'attr.orbox.resolution'?: string,
        'attr.orbox.resolution.passed'?: boolean,
        'attr.orbox.checked_at'?: string,
        'attr.orbox.error'?: string,
        // 3. Linza
        'attr.linza.checked'?: boolean,
        'attr.linza.passed'?: boolean,
        'attr.linza.report'?: string,
        'attr.linza.checked_at'?: string,
        'attr.linza.error'?: string,
    },
    cards: []
}

export interface SdnMetaRequest {
    scope?: string[],           // - list[str], необязательный, список префиксов где искать
    disk?: number,              // - int, необязательный, искать только на указанном диске
    bucket?: string,            // - int, необязательный, искать только в указанном макете
    bucketname?: string[],         // - int, необязательный, искать только в указанном макете
    file?: string[],            // - list[str], необязательный, список файлов по которым искать поиск идет filename* (файлы, это начинающиеся с / без бакета)
    prefix?: string[],          // - list[str], необязательный, списко префиксов без бакета, поиск аналогично файлам
    recursive?: boolean,        // - bool, необязательный искать по prefix или file только в пределах одной директории или нет
    extension?: string[],       // - list[str], необязательный, список расширений файлов ['mp4', 'txt']
    contenttype?: string[],     // - list[str], необязательный, content-type объекта
    attrname?: string[],        // - list[str], необязательный, поиск по наличию в метаданных указанных аттрибутов
    attr?: null,                // - dict, необязательный, поиск по наличию в метаданных атрибута и значения: { 'attrname1': 'value1', 'attrname2': 'value2', ... }
    attrvalues?: null,          // - dict, необязательный, поиск по наличию в метаданных ключа и нескольких возможных значений: { 'attrname1': ['value1', 'value2'], 'attrname2': ['value2'], }
    partattrname?: string,      // - list[str], необязательный, поиск по частичному совпадению названия аттрибута
    card?: number[],            // - list[int], необязательный, поиск объектов, привязанных к карточке
    cat?: FSObjectCategory,     // - int, необязательный, поиск по категории файла (cover, trailer, media, ...)
    defaultcat?: FSObjectCategory,  // - int, необязательный, defaultcat=3 - поиск трейлеров, привязанных к карточке
    offset?: number,            // - int, необязательный, листание, default=0
    limit?: number,             // - int, необязательный, листание, default=100
}

export interface SdnObject {
    disk_id: number,
    account: {
        id: number,
        rtype: number,
        name: string,
        nickname: string,
        org: {
            id: number,
            name: string,
            taxnum: string
        },
        owner: {
            id: number,
            email: string,
            phone: string,
            surname: string,
            hide_fio: boolean,
            lastname: string,
            firstname: string,
            hide_email: boolean,
            hide_phone: boolean
        },
        logo: string | null
    },
    bucket: string,
    filenames: string[],
    shared: boolean,
}

export interface SdnOperation {
    id: string, //- идентификатор задачи
    status: SdnOperationTaskStatus, //- статус задачи в TaskManager-е
    metadata: { //- метаданные
        ua: number,
        queue: string,
        command: SdnOperationType,
        source: string,
        destination: string,
        patterns: string[]
    },
    messages: (SdnOperationMessageInfo | SdnOperationMessageError | SdnOperationMessageActionRequired)[] // - сообщения, накопившиеся с последнего вызова GET по задаче
}

export interface SdnOperationBaseMessage {
    id: string,
    added: string,
    type: SdnOperationMessageType,
    code: SdnOperationCode, // SdnOperationCodes
    msg: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params?: any
}

export interface SdnOperationMessageInfo extends SdnOperationBaseMessage {
    type: 'info',
    // params?: {
    //     size: number,
    //     source: string,
    //     transferred: number,
    //     overall_size: number,
    //     overall_transferred: number
    // },
}

export interface SdnOperationMessageError extends SdnOperationBaseMessage {
    type: 'error',
    // params: null,
    details: string, // - детализация, возможно многострочная(\n)
}

export interface SdnOperationMessageActionRequired extends SdnOperationBaseMessage {
    type: 'action-required',
    params: {
        target: string,
    },
    timeout: number, //- Таймаут операции (отсчитывается с момента added)
    actions: {//- Доступные пользователю действия
        id: string, //- Идентификатор действия, который нужно указать в ответном сообщении, при выборе требуемого действия
        name: string, //- Отображаемое название действия
    }[],
    action: string, //- Действие, которое будет выполнено по умолчанию, по истечении таймаута
    details: string, // - детализация, возможно многострочная(\n)
}

export interface SdnOperationMessageParamsListZip {
    listing: {
        size: number,
        filename: string,
        compressed: number,
    }[],
    source: string,
}

export type SdnOperationMessageType = 'info' | 'error' | 'action-required';

export enum SdnOperationTaskStatus {
    UNKNOWN = 0, // _("MQ-TASK-STATUS-UNKNOWN", "Статус не определен")
    PENDING = 1, // _("MQ-TASK-STATUS-PENDING", "Ожидает выполнения")
    STARTED = 2, // _("MQ-TASK-STATUS-STARTED", "Выполняется")
    RETRY = 3, // _("MQ-TASK-STATUS-RETRY", "Ожидает перезапуска")
    FAILURE = 4, // _("MQ-TASK-STATUS-FAILURE", "Завершена с ошибками")
    SUCCESS = 5, // _("MQ-TASK-STATUS-SUCCESS", "Успешно завершена")
    REVOKED = 6, // _("MQ-TASK-STATUS-REVOKED", "Отменена")
}

export enum SdnOperationCode {
    STARTED = 'SDN-OP-000001',
    COMPLETED = 'SDN-OP-000002',
    ACCESS_FAILED = 'SDN-OP-000003',
    ERROR = 'SDN-OP-000004',
    FILE_NOT_DIRECTORY = 'SDN-OP-000005',
    NOT_FOUND_OR_PERMISSION_DENIDED = 'SDN-OP-000006',
    ZIP_LIST = 'SDN-OP-000007',
    ZIP_NOT_DIRECTORY = 'SDN-OP-000008',
    FILE_EXISTS = 'SDN-OP-000009',
    BAD_REQUEST = 'SDN-OP-000010',
    PERMISSION_DENIDED = 'SDN-OP-000011',
    PROGRESS = 'SDN-OP-000012',
    COPY_ERROR = 'SDN-OP-000013',
    DELETE_ERROR = 'SDN-OP-000014',
    DELETE_PROGRESS = 'SDN-OP-000015',
    DELETE_ERRORS = 'SDN-OP-000016',
    CANCELLED = 'SDN-OP-000017',
    ZIP_NOT_FOUND = 'SDN-OP-000018',
}

export const SdnOperationCodes = {
    'SDN-OP-000001': 'Операция запущена',
    // params: null
    'SDN-OP-000002': 'Операция завершена',
    // params: null
    'SDN-OP-000003': 'Не удается получить временный доступ к СХД для пользователя',
    // params: null
    'SDN-OP-000004': 'Возникла ошибка при выполнении операции',
    // params: null
    'SDN-OP-000005': 'Для просмотра содержимого архива нужно указать файл с архивом, а не директорию',
    // params: {
    // 	"source": "s3://...."
    // }
    'SDN-OP-000006': 'Указанный файл не найден, либо у Вас нет к нему доступа',
    // params: {
    // 	"source": "s3://...."
    // }
    'SDN-OP-000007': 'Список файлов архива',
    // params: {
    // 	"source": "s3://...."
    // 	"listing": [
    // 		{
    // 			"filename":   - название файла в архиве (вместе с путями)
    // 			"size":  	  - размер распакованного
    // 			"compressed": - размер сжатый
    // 		},
    // 		...
    // 	]
    // }
    'SDN-OP-000008': 'Для распаковки архива нужно указать файл с архивом, а не директорию',
    // params: {
    // 	"source": "s3://...."
    // }
    'SDN-OP-000009': 'Сохраняемый файл уже существует',
    // params: {
    // 	"target": "s3://...."
    // }
    'SDN-OP-000010': 'Указаны некорректные параметры для операции',
    // params: null
    'SDN-OP-000011': 'У Вас недостаточно прав доступа для записи файла',
    // params: {
    // 	"target": "s3://...."
    // }
    'SDN-OP-000012': 'Прогресс загрузки',
    // params: {
    // 	"source":              - копируемый файл
    // 	"size":                - размер копируемого файла
    // 	"transferred":         - скопировано по данному файлу
    // 	"overall_size":        - общий размер копируемого добра
    // 	"overall_transferred": - всего скопировано
    // }
    'SDN-OP-000013': 'Возникла ошибка при копировании файла',
    // params: {
    // 	"source": "s3://...."
    // 	"target": "s3://...."
    // }
    'SDN-OP-000014': 'Возникла ошибка при удалении файла',
    // вроде не используется как код в сообщениях
    'SDN-OP-000015': 'Прогресс удаления',
    // params: {
    // 	"overall_count":		- Общее кол0во удаляемых файлов
    // 	"overall_processed":	- Удалено
    // }
    'SDN-OP-000016': 'Возникли ошибки при удалении следующих файлов:\n{errors}',
    // вроде не используется как код в сообщениях
    'SDN-OP-000017': 'Операция прервана',
    // params: null
    'SDN-OP-000018': 'В архиве не найдено указанных файлов',
    // params: {
    // 	"source": "s3://...."
    // 	"patterns": [...]
    // }
}

export interface SdnOperationResponse {
    id: string, // - Идентификатор задачи
    metadata: { // - метаданные задачи
        ua: number,
        queue: string,
        command: SdnOperationType,
        source: string,
        destination: string,
        patterns: string[],
    }
}

export enum SdnOperationType {
    COPY = 'copy', //  _('SDN-OP-COPY', 'Копирование')
    SAVETODISK = 'savetodisk', // _('SDN-OP-SAVETODISK', 'Сохранение на диск')
    MOVE = 'move', //  _('SDN-OP-MOVE', 'Перемещение')
    RENAME = 'rename', //  _('SDN-OP-RENAME', 'Переименование')
    DELETE = 'delete', //  _('SDN-OP-DELETE', 'Удаление')
    LISTZIP = 'list-zip', //  _('SDN-OP-LISTZIP', 'Просмотр архива')
    UNZIP = 'unzip', //  _('SDN-OP-UNZIP', 'Распаковка архива')
    ZIP = 'zip', //  _('SDN-OP-ZIP', 'Создание архива')
}

export const SDN_OPERATION_TYPES = {
    [SdnOperationType.COPY]: 'storage:OPERATION_COPY',
    [SdnOperationType.SAVETODISK]: 'storage:OPERATION_COPY',
    [SdnOperationType.MOVE]: 'storage:OPERATION_MOVE',
    [SdnOperationType.RENAME]: 'storage:OPERATION_RENAME',
    [SdnOperationType.DELETE]: 'storage:OPERATION_DELETE',
    [SdnOperationType.LISTZIP]: 'storage:OPERATION_LISTZIP',
    [SdnOperationType.UNZIP]: 'storage:OPERATION_UNZIP',
    [SdnOperationType.ZIP]: 'storage:OPERATION_ZIP',
};

export interface SdnPermission {
    id: number,
    filename: string,         // - полное название директории или файла, включаем / с начала, если выдается для директории, на конце тоже /
    user?: {                      // - useraccount_id, для кого выдано разрешение, может быть либо пользователь, либо группа
        id: string,
        user_id: number,
        account_id: number,
        nickid: string | null,
        email: string,
        hide_email: boolean,
        firstname: string,
        lastname: string,
        surname: string | null,
        hide_fio: boolean
    },
    group?: {
        id: number,
        account: number,
        name: string,
        displayname: string
    } | null,
    perm: SdnPermissionType, // - разрешение
    reference: SdnReference[]
}

export interface SdnPermissionRequest {
    id?: string
    filename: string,
    user?: string,
    group?: number,
    perm: SdnPermissionType
}

export interface SdnSubject {
    users: { // - здесь будут пользователи аккаунта, в котором работает пользователь
        id: string,
        user_id: number,
        account_id: number,
        nickid: string | null,
        email: string,
        hide_email: boolean,
        firstname: string,
        lastname: string,
        surname: string | null,
        hide_fio: boolean
    }[],
    groups: { // - здесь перечислены, как предопределенные группы для аккаунта пользователя, так и группы (all) других аккаунтов
        id: number,
        account: number,
        name: string,
        displayname: string
    }[]
}

export const sdnApi = commonApi.injectEndpoints({
    endpoints: (builder) => ({
        // 3. Получение временного accesskey:
        getTemporalAccessKey: builder.mutation<StatusResponse<AccessKeyResponse>, void>({
            query() {
                return {
                    url: 'be/api/v1/sdn/iam/sts/',
                    method: 'POST',
                };
            },
        }),
        // 4. Управление постоянными accesskey:
        // GET, POST /be/api/v1/sdn/iam/svcacct/
        // DELETE /be/api/v1/sdn/iam/svcacct/<accesskey>/
        // Создание доступно администратору аккаунта, при создании он может указать пользователя аккаунта, для которого создать ключ
        // Операторам видны выданные им accesskey и они могут их удалить
        getPermanentAccessKeys: builder.query<StatusResponse<AccessPermanentKeyResponse[]>, void>({
            query: () => 'be/api/v1/sdn/iam/svcacct/',
            providesTags: [{ type: 'Access' }],
        }),
        createPermanentAccessKeys: builder.mutation<StatusResponse<AccessPermanentKeyResponse>, { user?: number, group?: number, ttl?: number } | void>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/iam/svcacct/',
                    method: 'POST',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Access' }]
        }),
        createAccessKeysFromInvite: builder.mutation<StatusResponse<AccessPermanentKeyResponse>, { invite: string }>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/iam/svcacct/',
                    method: 'POST',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Access' }]
        }),
        createInviteAccessKeys: builder.mutation<StatusResponse<AccessInviteKeyResponse>, { user?: number, ttl?: number } | void>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/iam/svcacct/invite/',
                    method: 'POST',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Access' }]
        }),
        createInviteResend: builder.mutation<StatusResponse<AccessInviteKeyResponse>, { user: number }>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/iam/svcacct/invite/resend/',
                    method: 'POST',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Access' }]
        }),
        deletePermanentAccessKeys: builder.mutation<void, string>({
            query: (accesskey) => ({
                url: `be/api/v1/sdn/iam/svcacct/${accesskey}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Access' }]
        }),
        // 5. Управление группами: !!Пока вроде не нужно реализовывать:
        getAccessGroups: builder.query<StatusResponse<AccessGroup>, void>({
            query: () => 'be/api/v1/sdn/iam/groups/',
        }),
        // 6. Права доступа:
        getSdnPermissions: builder.query<StatusResponse<SdnPermission[]>, void>({
            query: () => 'be/api/v1/sdn/permissions/',
            providesTags: [{ type: 'Access' }],
        }),
        getSdnPermission: builder.query<StatusResponse<SdnPermission>, string>({
            query: (id) => `be/api/v1/sdn/permissions/${id}`,
        }),
        createSdnPermission: builder.mutation<StatusResponse<SdnPermission>, SdnPermissionRequest>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/permissions/',
                    method: 'POST',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Access' }]
        }),
        updateSdnPermission: builder.mutation<StatusResponse<SdnPermission>, SdnPermissionRequest>({
            query(data) {
                return {
                    url: `be/api/v1/sdn/permissions/${data.id}/`,
                    method: 'PATCH',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Access' }]
        }),
        deleteSdnPermission: builder.mutation<void, string>({
            query: (id) => ({
                url: `be/api/v1/sdn/permissions/${id}/`,
                body: { force: true },
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Access' }]
        }),
        // Список пользователей и групп, которым можно назначить права:
        getSdnSubjects: builder.query<StatusResponse<SdnSubject>, void>({
            query: () => 'be/api/v1/sdn/subjects/',
        }),
        // Список доступных пользователю дисков (бакетов)
        getSdnDisks: builder.query<StatusResponse<SdnDisk[]>, void>({
            query: () => 'be/api/v1/sdn/disks/',
        }),
        // Список файлов и директорий, на которые у пользователя прописаны какие-либо права, с разбивкой по дискам (бакетам), теоретически можно использовать как старт для "Доступных"
        getSdnObjects: builder.query<StatusResponse<SdnObject[]>, void | string>({
            query: (disk_id) => `be/api/v1/sdn/objects/${disk_id ? '?disk=' + disk_id : ''}`,
        }),
        // Действующие права доступа пользователя к файлам на диске аккаунта, в котором он работает
        getSdnObjectsPermission: builder.mutation<StatusResponse<{ disk: number, filename: string, perm: SdnPermissionType }[]>, { files: string[], disk?: number }>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/objects/permissions/',
                    method: 'POST',
                    body: data
                };
            },
        }),
        getSdnConfig: builder.query<StatusResponse<{ enabled: boolean, s3api: string, 'access-type': 'path-style' | 'domain-style' }>, void>({
            query: () => 'be/api/v1/sdn/config/',
        }),
        // Операции копирования/переименования/перемещения/удаления/просмотра и извлечения zip архивов
        // Операции выполняются в фоновом режиме, посредством системы TaskManager (отдельная система)
        // В ходе выполнения генерируются сообщения об процессе выполнения (информационные/ошибки/запрос действия пользователя)
        getSdnOperations: builder.query<StatusResponse<SdnOperation[]>, void>({
            query: () => 'be/api/v1/sdn/operations/',
            providesTags: [{ type: 'Operation' }],
        }),
        getSdnOperation: builder.query<StatusResponse<SdnOperation>, string>({
            query: (uuid) => `be/api/v1/sdn/operations/${uuid}/`,
        }),
        createSdnOperation: builder.mutation<StatusResponse<SdnOperation>, SdnCommand>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/operations/',
                    method: 'POST',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Operation' }]
        }),
        replyToSdnOperation: builder.mutation<StatusResponse<SdnOperation>, { id: string, action: string, replyto: string }>({
            query: (data) => ({
                url: `be/api/v1/sdn/operations/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [{ type: 'Operation' }]
        }),
        // Задачу можно удалить (с принудительным прекращением)
        deleteSdnOperation: builder.mutation<void, string>({
            query: (id) => ({
                url: `be/api/v1/sdn/operations/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Operation' }],
        }),
        // Логирование событий в sdn
        getSdnAudit: builder.query<StatusPagesResponse<SdnAudit>, OffsetLimit>({
            query: (params) => `be/api/v1/sdn/audit/?${Object.keys(params).filter(k => params[k] || params[k] === 0).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
        }),
        // Вывод сводной информации по количеству операций над префиксами
        getSdnPrefixesAudit: builder.mutation<StatusResponse<{ prefix: string, operation: AuditOpType, opcount: number, }[]>, { prefixes: string[] }>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/audit/summary/',
                    method: 'POST',
                    body: data
                };
            },
        }),
        // Генерация presigned url через бекенд
        getSdnLinks: builder.query<StatusResponse<SdnLinkMin[]>, void>({
            query: () => 'be/api/v1/sdn/links/',
            providesTags: [{ type: 'Link' }],
        }),
        getSdnLink: builder.query<StatusResponse<SdnLink>, string>({
            query: (id) => `be/api/v1/sdn/links/${id}/`,
        }),
        createSdnLink: builder.mutation<StatusResponse<SdnLink>, SdnLinkRequest>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/links/',
                    method: 'POST',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Link' }]
        }),
        updateSdnLink: builder.mutation<StatusResponse<SdnLink>, Partial<SdnLinkRequest>>({
            query(data) {
                return {
                    url: `be/api/v1/sdn/links/${data.id}/`,
                    method: 'PATCH',
                    body: data
                };
            },
            invalidatesTags: [{ type: 'Link' }]
        }),
        deleteSdnLink: builder.mutation<void, string>({
            query: (id) => ({
                url: `be/api/v1/sdn/links/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Link' }]
        }),
        resendSdnLink: builder.mutation<StatusResponse<SdnLink>, { id: number, email: string }>({
            query({ id, email }) {
                return {
                    url: `be/api/v1/sdn/links/${id}/notify/`,
                    method: 'POST',
                    body: { email }
                };
            },
            invalidatesTags: [{ type: 'Link' }]
        }),
        qrSdnLink: builder.mutation<StatusResponse<SdnLink>, { id: number, email: string }>({
            query({ id, email }) {
                return {
                    url: `be/api/v1/sdn/links/${id}/notify/`,
                    method: 'POST',
                    body: { email }
                };
            },
            invalidatesTags: [{ type: 'Link' }]
        }),
        getSdnLinkAuthorize: builder.query<StatusResponse<SdnLinkAuth>, void>({
            query: () => 'be/api/v1/sdn/links/authorize/',
            providesTags: [{ type: 'Link' }],
        }),
        sendOTPSdnLink: builder.mutation<StatusResponse<SdnLinkOTP>, void>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/links/authorize/otp/send/',
                    method: 'POST',
                    body: data
                };
            },
        }),
        verifyOTPSdnLink: builder.mutation<StatusResponse<SdnLinkAuth>, string>({
            query(token) {
                return {
                    url: 'be/api/v1/sdn/links/authorize/otp/verify/',
                    method: 'POST',
                    body: { token }
                };
            },
        }),
        authorizeSdnLink: builder.mutation<StatusResponse<SdnLinkAuth>, string>({
            query(link) {
                return {
                    url: 'be/api/v1/sdn/links/authorize/user/',
                    method: 'POST',
                    body: { link }
                };
            },
        }),
        getSdnLinkPublic: builder.query<StatusResponse<SdnLinkPublic>, string>({
            query: (params) => `be/api/v1/sdn/links/get/?${params}`,
        }),
        // Получение метаданных: Вобщем то мне кажется, пока достаточно просто указывать scope и получать метаданные для того что отображается на экране у пользователя.
        getSdnMetadata: builder.mutation<StatusResponse<SdnMeta[]>, SdnMetaRequest>({
            query(data) {
                return {
                    url: 'be/api/v1/sdn/objects/metadata/',
                    method: 'POST',
                    body: data
                };
            },
        }),
        // Построение отчетов по метаданным Linza и Orbox:
        getSdnReport: builder.query<string, { prefix: string, reportType: string, created: string }>({
            query: ({ prefix, reportType, created }) => ({
                url: `be/api/v1/sdn/objects/report/?prefix=${prefix}&report=${reportType}&created=${created}`,
                responseType: 'blob',
                responseDataHandler: async (data: BlobPart) => {
                    const blob = new Blob([data], { type: 'application/pdf' })
                    return URL.createObjectURL(blob);
                },
                cache: 'no-cache',
            }),
        }),
        getSdnArchive: builder.query<Blob, { prefixes: string[], filename: string }>({
            query: ({ prefixes, filename }) => ({
                url: `be/api/v1/sdn/archive/zip/?${prefixes.map((p) => `prefixes=${p}`).join('&')}&filename=${filename}`,
                responseType: 'blob',
                cache: 'no-cache',
            }),
        }),
    }),
});

export const {
    useCreatePermanentAccessKeysMutation,
    useDeletePermanentAccessKeysMutation,
    useGetAccessGroupsQuery,
    useGetPermanentAccessKeysQuery,
    useGetTemporalAccessKeyMutation,
    useGetSdnPermissionsQuery,
    useGetSdnPermissionQuery,
    useCreateSdnPermissionMutation,
    useUpdateSdnPermissionMutation,
    useDeleteSdnPermissionMutation,
    useGetSdnConfigQuery,
    useGetSdnSubjectsQuery,
    useGetSdnDisksQuery,
    useGetSdnObjectsQuery,
    useGetSdnObjectsPermissionMutation,
    useLazyGetAccessGroupsQuery,
    useLazyGetPermanentAccessKeysQuery,
    useLazyGetSdnPermissionsQuery,
    useLazyGetSdnPermissionQuery,
    useLazyGetSdnSubjectsQuery,
    useLazyGetSdnObjectsQuery,
    useLazyGetSdnDisksQuery,
    useLazyGetSdnConfigQuery,
    useGetSdnOperationQuery,
    useGetSdnOperationsQuery,
    useLazyGetSdnOperationQuery,
    useLazyGetSdnOperationsQuery,
    useCreateSdnOperationMutation,
    useDeleteSdnOperationMutation,
    useReplyToSdnOperationMutation,
    useLazyGetSdnAuditQuery,
    useGetSdnAuditQuery,
    useGetSdnPrefixesAuditMutation,
    useGetSdnLinkQuery,
    useGetSdnLinksQuery,
    useLazyGetSdnLinkQuery,
    useLazyGetSdnLinksQuery,
    useCreateSdnLinkMutation,
    useDeleteSdnLinkMutation,
    useGetSdnMetadataMutation,
    useGetSdnLinkPublicQuery,
    useLazyGetSdnLinkPublicQuery,
    useGetSdnReportQuery,
    useLazyGetSdnReportQuery,
    useGetSdnArchiveQuery,
    useLazyGetSdnArchiveQuery,
    useUpdateSdnLinkMutation,
    useQrSdnLinkMutation,
    useResendSdnLinkMutation,
    useLazyGetSdnLinkAuthorizeQuery,
    useGetSdnLinkAuthorizeQuery,
    useSendOTPSdnLinkMutation,
    useVerifyOTPSdnLinkMutation,
    useAuthorizeSdnLinkMutation,
    useCreateInviteAccessKeysMutation,
    useCreateInviteResendMutation,
    useCreateAccessKeysFromInviteMutation,
} = sdnApi;
